import React from "react";
import { Heart, Brain, Moon, Wind, Activity, BookOpen } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";

const MARKERS = [
  {
    icon: Heart,
    marker: "HRV",
    label: "Heart Rate Variability",
    body:
      "A validated non-invasive index of autonomic balance. Widens with parasympathetic capacity and narrows under chronic sympathetic load — a primary tracked variable in Adaptive Physiology™.",
    testId: "science-marker-hrv",
  },
  {
    icon: Wind,
    marker: "Vagal Tone",
    label: "Parasympathetic Access",
    body:
      "Governs the speed of return to a regulated state after activation. Trainable through rhythmic aerobic loading, respiration work, and structured recovery.",
    testId: "science-marker-vagal",
  },
  {
    icon: Brain,
    marker: "BDNF",
    label: "Neuroplasticity Substrate",
    body:
      "Brain-derived neurotrophic factor supports the encoding of new neural patterns. Exercise is one of the most robustly documented inputs to BDNF expression.",
    testId: "science-marker-bdnf",
  },
  {
    icon: Activity,
    marker: "Cortisol Rhythm",
    label: "Stress Calibration",
    body:
      "Adaptive Physiology aims to normalize the diurnal cortisol curve rather than blunt it — recalibrating the stress response instead of suppressing it.",
    testId: "science-marker-cortisol",
  },
  {
    icon: Moon,
    marker: "Sleep Architecture",
    label: "Recovery & Consolidation",
    body:
      "Deep and REM stages are where autonomic recovery and memory consolidation occur. Structured daytime physiology is one of the most reliable levers on both.",
    testId: "science-marker-sleep",
  },
  {
    icon: BookOpen,
    marker: "Behavioral Signal",
    label: "Real-World Output",
    body:
      "The physiology is measured; the behavior is observed. When markers move together — physiological set-point and lived behavior — the loop is working.",
    testId: "science-marker-behavior",
  },
];

const REFERENCES = [
  "Porges — Polyvagal Theory & vagal regulation of autonomic state",
  "Thayer & Lane — Neurovisceral integration model of HRV",
  "Ratey — Exercise, BDNF, and neuroplasticity",
  "McEwen — Allostasis, allostatic load, and stress physiology",
  "Sapolsky — Chronic stress and endocrine adaptation",
  "Kandel — Cellular mechanisms of learning and memory",
  "Van der Kolk — Somatic mechanisms in trauma physiology",
  "Buysse / Walker — Sleep architecture, autonomic recovery, and behavior",
];

const Science = () => {
  return (
    <PageShell>
      <SubHero
        testId="science-hero"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Methodology", to: "/methodology" },
          { label: "The Science" },
        ]}
        overline="Research & Scientific Foundations"
        headline="Adaptive Physiology is built on measurable physiology,"
        headlineAccent="not proprietary claims."
        lead="The methodology synthesizes and applies established research from exercise physiology, autonomic neuroscience, behavioral science, and stress physiology. This page maps the primary mechanisms it targets."
      />

      <section
        data-testid="science-markers-section"
        className="relative py-24 sm:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="01"
              label="Primary Physiological Markers"
              testId="science-markers-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-3xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              The variables Adaptive Physiology™ observes, targets, and trains.
            </h2>
          </Reveal>

          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MARKERS.map((m, i) => (
              <Reveal delay={i * 0.06} key={m.marker}>
                <div
                  data-testid={`card-${m.testId}`}
                  className="forr-card rounded-[24px] p-8 h-full"
                >
                  <div className="w-11 h-11 rounded-2xl bg-teal-tint border border-teal-soft flex items-center justify-center forr-text-teal-deep mb-8">
                    <m.icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="font-display forr-text-teal-deep text-xl font-medium tracking-tight">
                      {m.marker}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.24em] forr-text-mute">
                      {m.label}
                    </span>
                  </div>
                  <p className="mt-4 text-sm font-light forr-text-soft leading-relaxed">
                    {m.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="science-basis-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-5">
              <Reveal>
                <SectionOverline
                  number="02"
                  label="Foundational Bodies of Research"
                  testId="science-basis-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Standing on established science.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-md forr-text-soft font-light leading-relaxed">
                  Adaptive Physiology™ synthesizes existing peer-reviewed research
                  from multiple established fields. It is a methodology of applied
                  integration — not a claim of original scientific discovery.
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:pl-8">
              <Reveal delay={0.1}>
                <div className="rounded-[24px] border border forr-surface p-8 sm:p-10">
                  <div className="text-[11px] uppercase tracking-[0.28em] forr-text-teal-deep mb-6">
                    Referenced Fields & Researchers
                  </div>
                  <ul className="space-y-4">
                    {REFERENCES.map((r, i) => (
                      <li
                        key={r}
                        className="flex items-start gap-4 text-sm forr-text-soft font-light border-b border pb-4 last:border-0"
                      >
                        <span className="font-display forr-text-teal-deep text-xs mt-0.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-8 text-xs forr-text-mute leading-relaxed">
                    A cited literature index will be published as
                    <span className="forr-text-teal-deep"> /research/literature</span> in
                    a forthcoming release of the site.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="science-position-section"
        className="relative py-24 sm:py-32 border-t border"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="max-w-4xl">
              <SectionOverline
                number="03"
                label="Scientific Posture"
                testId="science-posture-overline"
              />
              <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                Evidence-informed. Outcome-honest.
              </h2>
              <p className="mt-8 text-lg font-light forr-text-soft leading-relaxed">
                We do not overstate. Adaptive Physiology™ is a framework of
                applied integration — the ways it is delivered are informed by
                published research, and the outcomes it tracks are physiological
                measurements, not marketing metrics.
              </p>
              <p className="mt-6 text-lg font-light forr-text-soft leading-relaxed">
                Where the science is settled, we apply it directly. Where it is
                still evolving, we say so. That posture is a feature of the
                methodology, not a compromise.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <NextStep
        overline="Next"
        title="How It Works"
        description="From physiological markers to a closed-loop protocol — the operational cadence of Adaptive Physiology™."
        to="/methodology/how-it-works"
        cta="See the protocol"
        testId="science-next-step"
      />
    </PageShell>
  );
};

export default Science;
